/**
 * ─── Jewelry Shop Schema — DDD Aggregate Design ──────────────
 * Philippine jewelry retail: inventory priced by metal weight × rate,
 * POS with old-gold trade-ins & installments, repair/custom jobs,
 * consignment, KYC/AMLA compliance.
 *
 * Bounded Contexts (7):
 *  1. Catalog & Inventory  — aggregate: JewelryItem
 *  2. Pricing & Rates       — aggregate: RateCard, service: PriceCalculator
 *  3. Sales / POS           — aggregate: SalesOrder
 *  4. Job Orders            — aggregate: RepairJob
 *  5. Consignment           — aggregate: Consignment
 *  6. Customer & Compliance — aggregate: Customer, entity: KYCRecord
 *  7. Reporting             — read-side only
 *
 * Ubiquitous Language (Part 2.1):
 *   Tag/SKU, Purity (karat/fineness), Making Charge, Wastage,
 *   Rate Card, Old-Gold Exchange ("palit-luma"), Job Order,
 *   Consignment, Covered Transaction (AMLA)
 *
 * Invariants:
 *  - Weight & purity immutable after intake (except audit-logged correction)
 *  - Sale-line price is a snapshot — never recomputed from a later rate_card
 *  - RepairJob follows strict state machine, not free-text status
 *  - KYC triggered at configurable AMLA cash threshold (default ₱1,000,000)
 *  - Every table has tenant_id from day one
 */

import { z } from 'zod'
import { notesSchema, createQuerySchema, createUpdateSchema, emailSchema, phoneSchema } from '@repo/core'

// ═══════════════════════════════════════════════════════════════
// Value Objects
// ═══════════════════════════════════════════════════════════════

/** Immutable weight value object */
export interface Weight {
  grams: number                 // precise to 3 decimal places
  unit: 'grams' | 'oz' | 'tola'
}

/** Metal purity — karat (10-24) or fineness (333-999) */
export type PurityUnit = 'karat' | 'fineness'

export interface Purity {
  value: number                 // e.g. 18 for 18K, 750 for 18K fineness
  unit: PurityUnit
}

export interface StoneSpec {
  type: string                  // diamond, ruby, sapphire, etc.
  carat: number
  clarity?: string              // IF, VVS, VS, SI, I...
  color?: string                // D-Z for diamonds
  cut?: string                  // Excellent, Very Good, Good...
  certNumber?: string           // GIA/IGI certificate number
}

export interface Money {
  amount: number
  currency: 'PHP' | 'USD'
}

// ═══════════════════════════════════════════════════════════════
// 1. Catalog & Inventory — JewelryItem (Aggregate Root)
// ═══════════════════════════════════════════════════════════════

export type ItemCategory = 'ring' | 'necklace' | 'bangle' | 'bracelet' | 'earring' | 'pendant' | 'chain' | 'set' | 'other'
export type MetalType = 'gold' | 'silver' | 'platinum' | 'palladium' | 'mixed'
export type MakingChargeType = 'fixed' | 'per_gram' | 'percent'
export type ItemStatus = 'in_stock' | 'reserved' | 'sold' | 'in_repair' | 'consignment' | 'transferred' | 'returned'

export interface JewelryItem {
  id: string
  tenantId: string
  branchId?: string

  // ─── Identification ────────────────────────────────────
  tagNumber: string              // Unique per tenant — printed on price tag
  sku?: string                   // Optional secondary identifier
  description: string
  category: ItemCategory

  // ─── Metal Properties ──────────────────────────────────
  metalType: MetalType
  purityKarat: number            // e.g. 18 for 18K, 24 for 24K
  purityFineness?: number        // e.g. 750 for 18K
  weightGrams: number            // Precise to 0.001g

  // ─── Stones ─────────────────────────────────────────────
  primaryStone?: StoneSpec
  secondaryStones?: StoneSpec[]
  totalStoneCarats?: number

  // ─── Pricing Configuration ─────────────────────────────
  makingChargeType: MakingChargeType
  makingChargeValue: number      // Amount, per-gram rate, or percentage
  wastagePercent: number         // Melting-loss charge (% of metal value)
  costBasis: number              // What the shop paid for it

  // ─── Status & Location ─────────────────────────────────
  status: ItemStatus
  reservedForOrderId?: string
  showcaseLocation?: string      // e.g. "Showcase A, Shelf 3"
  vaultLocation?: string

  // ─── Media ──────────────────────────────────────────────
  photos?: string[]              // Array of photo URLs
  certificateUrl?: string        // Lab certificate PDF

  // ─── Metadata Extension ────────────────────────────────
  customAttributes: Record<string, unknown>

  // ─── Audit ──────────────────────────────────────────────
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const ItemCategorySchema = z.enum(['ring', 'necklace', 'bangle', 'bracelet', 'earring', 'pendant', 'chain', 'set', 'other'])
export const MetalTypeSchema = z.enum(['gold', 'silver', 'platinum', 'palladium', 'mixed'])
export const MakingChargeTypeSchema = z.enum(['fixed', 'per_gram', 'percent'])
export const ItemStatusSchema = z.enum(['in_stock', 'reserved', 'sold', 'in_repair', 'consignment', 'transferred', 'returned'])

export const CreateJewelryItemSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  tagNumber: z.string().min(1).max(50),
  description: z.string().min(1).max(500),
  category: ItemCategorySchema,
  metalType: MetalTypeSchema,
  purityKarat: z.number().min(10).max(24),
  weightGrams: z.number().min(0.001),
  makingChargeType: MakingChargeTypeSchema.default('per_gram'),
  makingChargeValue: z.number().min(0),
  wastagePercent: z.number().min(0).max(100).default(0),
  costBasis: z.number().min(0),
  photos: z.array(z.string()).optional(),
  customAttributes: z.record(z.unknown()).default({}),
  notes: notesSchema.optional(),
})

export const ITEM_CATEGORY_LABELS: Record<ItemCategory, string> = {
  ring: 'Ring', necklace: 'Necklace', bangle: 'Bangle', bracelet: 'Bracelet',
  earring: 'Earring', pendant: 'Pendant', chain: 'Chain', set: 'Set', other: 'Other',
}
export const METAL_TYPE_LABELS: Record<MetalType, string> = {
  gold: 'Gold', silver: 'Silver', platinum: 'Platinum', palladium: 'Palladium', mixed: 'Mixed',
}
export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  in_stock: 'In Stock', reserved: 'Reserved', sold: 'Sold',
  in_repair: 'In Repair', consignment: 'Consignment', transferred: 'Transferred', returned: 'Returned',
}
export const MAKING_CHARGE_LABELS: Record<MakingChargeType, string> = {
  fixed: 'Fixed', per_gram: 'Per Gram', percent: '% of Metal',
}

// ═══════════════════════════════════════════════════════════════
// 2. Pricing & Rates — RateCard (Aggregate Root)
// ═══════════════════════════════════════════════════════════════

export interface RateCard {
  id: string
  tenantId: string
  metalType: MetalType
  purityKarat: number             // 24 for pure gold, 18 for 18K, etc.
  ratePerGram: number             // Price per gram in PHP
  effectiveAt: number             // Timestamp when this rate takes effect
  source?: string                 // "manual", "api", "bangko_sentral"
  createdBy: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
}

export const CreateRateCardSchema = z.object({
  tenantId: z.string().min(1),
  metalType: MetalTypeSchema,
  purityKarat: z.number().min(10).max(24),
  ratePerGram: z.number().min(0),
  effectiveAt: z.number().default(() => Date.now()),
  source: z.string().max(100).optional(),
})

// ═══════════════════════════════════════════════════════════════
// 3. Sales / POS — SalesOrder (Aggregate Root)
// ═══════════════════════════════════════════════════════════════

export type OrderStatus = 'draft' | 'completed' | 'cancelled' | 'returned'
export type OrderLineType = 'sale' | 'old_gold_exchange' | 'return'
export type PaymentMethod = 'cash' | 'card' | 'gcash' | 'maya' | 'bank_transfer' | 'installment'
export type InstallmentStatus = 'active' | 'paid' | 'defaulted'

export interface SalesOrderLine {
  id: string
  salesOrderId: string
  jewelryItemId?: string
  tagNumber?: string
  description: string

  // ─── Price Snapshot (immutable after creation) ──────────
  weightGrams: number
  ratePerGramSnapshot: number
  metalValueSnapshot: number     // weight × rate × purity factor
  makingChargeSnapshot: number
  wastageSnapshot: number
  discountAmount: number

  lineTotal: number
  lineType: OrderLineType

  // ─── Old-Gold Exchange fields ──────────────────────────
  oldGoldWeight?: number
  oldGoldPurity?: number
  oldGoldAppraisalValue?: number

  createdAt: number
}

export interface SalesOrder {
  id: string                          // Aggregate root
  tenantId: string
  branchId?: string
  customerId?: string
  customerName?: string
  orderNumber: string                 // e.g. SO-20250701-0001
  status: OrderStatus

  // ─── Financials ────────────────────────────────────────
  lines: SalesOrderLine[]
  subtotal: number
  discountTotal: number
  oldGoldCreditTotal: number          // From old-gold exchange lines
  taxTotal: number
  grandTotal: number

  // ─── Payments ──────────────────────────────────────────
  paymentMethod: PaymentMethod
  amountTendered: number
  changeGiven: number
  installmentMonths?: number
  installmentMonthlyAmount?: number
  installmentStatus?: InstallmentStatus

  // ─── KYC / AMLA ────────────────────────────────────────
  requiresKyc: boolean
  kycRecordId?: string               // Created if cash transaction > AMLA threshold
  cashPortion: number                // How much was paid in cash

  // ─── Audit ──────────────────────────────────────────────
  salesAssociateId?: string
  salesAssociateName?: string
  completedAt?: number
  cancelledAt?: number
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const OrderStatusSchema = z.enum(['draft', 'completed', 'cancelled', 'returned'])
export const PaymentMethodSchema = z.enum(['cash', 'card', 'gcash', 'maya', 'bank_transfer', 'installment'])
export const InstallmentStatusSchema = z.enum(['active', 'paid', 'defaulted'])

export const CreateSalesOrderSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  customerId: z.string().optional(),
  customerName: z.string().max(200).optional(),
  lines: z.array(z.object({
    jewelryItemId: z.string().optional(),
    tagNumber: z.string().optional(),
    description: z.string().min(1),
    weightGrams: z.number().min(0),
    ratePerGramSnapshot: z.number().min(0),
    metalValueSnapshot: z.number().min(0),
    makingChargeSnapshot: z.number().min(0),
    wastageSnapshot: z.number().min(0),
    discountAmount: z.number().min(0).default(0),
    lineTotal: z.number().min(0),
    lineType: z.enum(['sale', 'old_gold_exchange', 'return']).default('sale'),
    oldGoldWeight: z.number().optional(),
    oldGoldPurity: z.number().optional(),
    oldGoldAppraisalValue: z.number().optional(),
  })),
  paymentMethod: PaymentMethodSchema,
  amountTendered: z.number().min(0),
  salesAssociateName: z.string().max(200).optional(),
  notes: notesSchema.optional(),
})

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  draft: 'Draft', completed: 'Completed', cancelled: 'Cancelled', returned: 'Returned',
}
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash', card: 'Card', gcash: 'GCash', maya: 'Maya', bank_transfer: 'Bank Transfer', installment: 'Installment',
}

// ═══════════════════════════════════════════════════════════════
// 4. Job Orders — RepairJob (Aggregate Root)
// ═══════════════════════════════════════════════════════════════

export type JobType = 'repair' | 'custom_order'
export type JobStatus = 'received' | 'appraised' | 'in_progress' | 'ready' | 'delivered' | 'cancelled'

/**
 * REPAIR JOB STATE MACHINE (Part 2.4):
 *   received → appraised → in_progress → ready → delivered
 *   Any state → cancelled (only if not yet delivered)
 *   delivered → (terminal, no further transitions)
 */
export const JOB_STATE_TRANSITIONS: Record<JobStatus, JobStatus[]> = {
  received: ['appraised', 'cancelled'],
  appraised: ['in_progress', 'cancelled'],
  in_progress: ['ready', 'cancelled'],
  ready: ['delivered', 'cancelled'],
  delivered: [],                    // Terminal
  cancelled: [],                    // Terminal
}

export interface RepairJob {
  id: string
  tenantId: string
  branchId?: string
  customerId: string
  customerName?: string
  jewelryItemId?: string            // If item from inventory
  jobNumber: string                 // e.g. RJ-20250701-0001
  jobType: JobType
  description: string
  status: JobStatus

  // ─── Cost Estimation ───────────────────────────────────
  estimatedCost: number
  actualCost?: number
  materialsCost?: number
  laborCost?: number

  // ─── Timeline ──────────────────────────────────────────
  receivedDate: number
  promisedDate?: number             // When customer was promised completion
  completedDate?: number
  deliveredDate?: number

  // ─── Details ────────────────────────────────────────────
  goldsmithName?: string
  workDescription?: string
  beforePhotos?: string[]
  afterPhotos?: string[]
  customerNotifiedAt?: number       // Last notification timestamp

  customAttributes: Record<string, unknown>

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const JobTypeSchema = z.enum(['repair', 'custom_order'])
export const JobStatusSchema = z.enum(['received', 'appraised', 'in_progress', 'ready', 'delivered', 'cancelled'])

export const CreateRepairJobSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  customerId: z.string().min(1),
  jewelryItemId: z.string().optional(),
  jobType: JobTypeSchema,
  description: z.string().min(1).max(1000),
  estimatedCost: z.number().min(0),
  promisedDate: z.number().optional(),
  receivedDate: z.number().default(() => Date.now()),
  goldsmithName: z.string().max(200).optional(),
  workDescription: z.string().max(2000).optional(),
  customAttributes: z.record(z.unknown()).default({}),
  notes: notesSchema.optional(),
})

export const JOB_TYPE_LABELS: Record<JobType, string> = { repair: 'Repair', custom_order: 'Custom Order' }
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  received: 'Received', appraised: 'Appraised', in_progress: 'In Progress',
  ready: 'Ready for Pickup', delivered: 'Delivered', cancelled: 'Cancelled',
}

// ═══════════════════════════════════════════════════════════════
// 5. Consignment
// ═══════════════════════════════════════════════════════════════

export type ConsignmentStatus = 'active' | 'settled' | 'closed'

export interface Consignment {
  id: string
  tenantId: string
  consignorName: string
  consignorContact?: string
  consignorAddress?: string
  commissionPercent: number        // e.g. 15 = 15% commission to shop
  status: ConsignmentStatus
  agreementDate: number
  settlementDate?: number
  totalItemsValue: number           // Sum of all consigned items' value
  totalSoldValue: number            // How much has been sold
  totalCommission: number           // Shop's commission on sold items

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const ConsignmentStatusSchema = z.enum(['active', 'settled', 'closed'])

export const CreateConsignmentSchema = z.object({
  tenantId: z.string().min(1),
  consignorName: z.string().min(1).max(200),
  consignorContact: z.string().max(100).optional(),
  consignorAddress: z.string().max(500).optional(),
  commissionPercent: z.number().min(0).max(100),
  agreementDate: z.number().default(() => Date.now()),
  notes: notesSchema.optional(),
})

export const CONSIGNMENT_STATUS_LABELS: Record<ConsignmentStatus, string> = {
  active: 'Active', settled: 'Settled', closed: 'Closed',
}

// ═══════════════════════════════════════════════════════════════
// 6. Customer & Compliance — Customer (Aggregate Root) + KYCRecord
// ═══════════════════════════════════════════════════════════════

export type LoyaltyTier = 'regular' | 'silver' | 'gold' | 'platinum'

export interface KYCRecord {
  id: string
  tenantId: string
  customerId: string
  documentType: string             // Driver's License, Passport, UMID, etc.
  documentNumber: string
  documentPhotoUrl?: string
  selfiePhotoUrl?: string
  verifiedAt?: number
  verifiedBy?: string
  transactionId: string             // The order that triggered KYC
  transactionAmount: number

  createdAt: number
}

export interface Customer {
  id: string
  tenantId: string
  branchId?: string
  customerCode: string
  fullName: string
  firstName: string
  lastName: string
  phone: string
  email?: string
  address?: string
  dateOfBirth?: string

  // ─── Loyalty ────────────────────────────────────────────
  loyaltyTier: LoyaltyTier
  totalPurchases: number
  totalOrders: number
  lastPurchaseDate?: number

  // ─── KYC / AMLA ────────────────────────────────────────
  kycVerified: boolean
  kycRecords?: KYCRecord[]

  customAttributes: Record<string, unknown>
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const LoyaltyTierSchema = z.enum(['regular', 'silver', 'gold', 'platinum'])

export const CreateCustomerSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  phone: phoneSchema,
  email: emailSchema.optional(),
  address: z.string().max(500).optional(),
  dateOfBirth: z.string().optional(),
  notes: notesSchema.optional(),
})

export const LOYALTY_TIER_LABELS: Record<LoyaltyTier, string> = {
  regular: 'Regular', silver: 'Silver', gold: 'Gold', platinum: 'Platinum',
}

// ═══════════════════════════════════════════════════════════════
// Query & Update Schemas
// ═══════════════════════════════════════════════════════════════

export const JewelryItemQuerySchema = createQuerySchema({})
export const RateCardQuerySchema = createQuerySchema({})
export const SalesOrderQuerySchema = createQuerySchema({})
export const RepairJobQuerySchema = createQuerySchema({})
export const ConsignmentQuerySchema = createQuerySchema({})
export const CustomerQuerySchema = createQuerySchema({})

export const UpdateJewelryItemSchema = createUpdateSchema({})
export const UpdateCustomerSchema = createUpdateSchema({})
